import { API_BASE, api } from '../lib/api';

export interface ValueList {
  id: number;
  listKey: string;
  name: string;
  isSystem: number;
}

export interface ValueListItem {
  id: number;
  listKey: string;
  label: string;
  sortOrder: number;
  isHidden: number;
  isSystemSeed: number;
  createdByUserId: number | null;
}

export interface ModuleRow {
  moduleKey: string;
  name: string;
  label: string;
  path: string;
  description: string | null;
  orderIndex: number;
  minPlanCode: string;
  enabled: number;
  isVisible: number;
}

export interface NavModule {
  moduleKey: string;
  label: string;
  path: string;
}

export interface SettingsUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
  role: string | null;
}

export interface InviteOrCreateUserPayload {
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'ADMIN' | 'EMPLOYEE';
}

export interface FortnoxSettings {
  clientId: string;
  hasClientSecret: boolean;
  redirectUri: string;
  scopes: string;
  isConnected: boolean;
  connectedAt: string | null;
  accessTokenExpiresAt: string | null;
  tokenScope: string | null;
}

export interface FortnoxSettingsUpdatePayload {
  clientId: string;
  clientSecret?: string;
  scopes?: string;
}

export interface WooCommercePreviewItem {
  wooId: number;
  sku: string | null;
  name: string;
  price: string;
  stockStatus: string;
  hasImage: boolean;
  imageUrl: string | null;
  imageAlt: string | null;
  status: 'matched' | 'new' | 'missing_sku';
  matchedProductId: number | null;
  matchedProductName: string | null;
  matchedProductSku: string | null;
}

export interface WooCommercePreview {
  storeUrl: string;
  totals: {
    wooProducts: number;
    matched: number;
    new: number;
    missingSku: number;
  };
  items: WooCommercePreviewItem[];
}

export interface ZettlePreviewItem {
  zettleProductUuid: string;
  zettleVariantUuid: string;
  sku: string | null;
  barcode: string | null;
  name: string;
  price: string;
  priceAmountMinor: number | null;
  currencyId: string | null;
  vatPercentage: string;
  imageUrl: string | null;
  categoryName: string | null;
  status: 'matched' | 'new' | 'missing_sku';
  matchMethod: 'sku' | 'name' | null;
  matchedProductId: number | null;
  matchedProductName: string | null;
  matchedProductSku: string | null;
}

export interface ZettlePreview {
  clientId: string;
  totals: {
    zettleProducts: number;
    matched: number;
    new: number;
    missingSku: number;
  };
  items: ZettlePreviewItem[];
}

export interface Gs1OverviewItem {
  productId: number;
  sku: string | null;
  ean: string | null;
  name: string;
  kind: string;
  categoryLabel: string | null;
  uomLabel: string | null;
  canSell: boolean;
  status: 'has_ean' | 'missing_ean';
}

export interface Gs1Overview {
  developerUrl: string;
  settings: {
    companyPrefix: string;
    itemRefDigits: number;
  };
  totals: {
    products: number;
    withEan: number;
    missingEan: number;
    sellableMissingEan: number;
    barcodePoolPlanned: number;
    availableNow: number;
  };
  nextAvailable: Array<{
    itemRef: string;
    gtin13: string;
  }>;
  items: Gs1OverviewItem[];
}

function notifyNavChanged(): void {
  window.dispatchEvent(new CustomEvent('apisai:nav-modules-changed'));
}

export const settingsApi = {
  getValueLists: () => api.get<{ valueLists: ValueList[] }>('/value-lists'),
  getValueListItems: (listKey: string) =>
    api.get<{ items: ValueListItem[] }>(`/value-lists/${encodeURIComponent(listKey)}/items`),
  createValueListItem: (listKey: string, data: { label: string; sortOrder?: number }) =>
    api.post<ValueListItem>(`/value-lists/${encodeURIComponent(listKey)}/items`, data),
  updateValueListItem: (id: number, data: { label?: string; sortOrder?: number; isHidden?: boolean }) =>
    api.patch<{ ok: boolean }>(`/value-lists/items/${id}`, data),
  deleteValueListItem: (id: number) => api.delete<{ ok: boolean }>(`/value-lists/items/${id}`),
  getModules: () => api.get<{ modules: ModuleRow[] }>('/modules'),
  getNavModules: () => api.get<NavModule[]>('/modules/nav'),
  updateBusinessModule: (moduleKey: string, data: { isVisible: boolean }) =>
    api.patch<{ ok: boolean }>(`/business-modules/${encodeURIComponent(moduleKey)}`, data).then((res) => {
      notifyNavChanged();
      return res;
    }),
  getUsers: () => api.get<{ users: SettingsUser[] }>('/users'),
  inviteOrCreateUser: (payload: InviteOrCreateUserPayload) =>
    api.post<{ ok: boolean; userId: number; createdUser: boolean; invited: boolean }>('/users/invite-or-create', payload),
  getUserModulePermissions: () =>
    api.get<{ permissions: Record<number, Record<string, string>> }>('/user-module-permissions'),
  setUserModulePermissions: (permissions: Array<{ userId: number; moduleKey: string; accessLevel: string }>) =>
    api.put<{ ok: boolean }>('/user-module-permissions', { permissions }),
  getPrinters: () => api.get<{ printers: Printer[] }>('/printers'),
  createPrinter: (data: { name: string; placement?: string; ipAddress?: string; notes?: string }) =>
    api.post<{ id: number; name: string }>('/printers', data),
  updatePrinter: (id: number, data: { name?: string; placement?: string; ipAddress?: string; notes?: string; isActive?: boolean }) =>
    api.patch<{ ok: boolean }>(`/printers/${id}`, data),
  deletePrinter: (id: number) => api.delete<{ ok: boolean }>(`/printers/${id}`),
  getBusinessPrintSettings: () =>
    api.get<BusinessPrintSettings>('/business-print-settings'),
  updateBusinessPrintSettings: (data: Partial<BusinessPrintSettings>) =>
    api.patch<{ ok: boolean }>('/business-print-settings', data),
  getCompanyHomeSettings: () => api.get<CompanyHomeSettings>('/company/home-settings'),
  uploadCompanyLogo: (payload: { filename: string; mimeType: string; contentBase64: string }) =>
    api.post<{ ok: boolean; logoMediaId: number }>('/company/logo', payload),
  getCompanyLogoUrl: () => `${API_BASE}/company/logo`,
  getFortnoxSettings: () => api.get<FortnoxSettings>('/fortnox/settings'),
  updateFortnoxSettings: (data: FortnoxSettingsUpdatePayload) => api.patch<{ ok: boolean }>('/fortnox/settings', data),
  startFortnoxConnect: () => api.post<{ authUrl: string }>('/fortnox/connect/start'),
  disconnectFortnox: () => api.post<{ ok: boolean }>('/fortnox/disconnect'),
  syncFortnoxArticles: () => api.post<{ ok: boolean; created: number; updated: number; total: number }>('/fortnox/sync-articles'),
  getWooCommercePreview: () => api.get<WooCommercePreview>('/woocommerce/preview'),
  getZettlePreview: () => api.get<ZettlePreview>('/zettle/preview'),
  getGs1Overview: () => api.get<Gs1Overview>('/gs1/overview'),
  updateGs1Settings: (data: { companyPrefix: string; itemRefDigits: number }) => api.patch<{ ok: boolean }>('/gs1/settings', data),
};

export interface Printer {
  id: number;
  name: string;
  placement: string | null;
  ipAddress: string | null;
  notes: string | null;
  isActive: number;
}

export interface BusinessPrintSettings {
  businessName: string | null;
  logoMediaId: number | null;
  contactName: string | null;
  contactAddress: string | null;
  address1: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  orgNr: string | null;
  vatNr: string | null;
  seat: string | null;
  hasFSkatt: boolean;
  bankgiro: string | null;
  email: string | null;
  web: string | null;
  phone: string | null;
  beekeepingStartDate: string | null;
  footerText: string | null;
  showApisAiBranding: boolean;
}

export interface CompanyHomeSettings {
  beekeepingStartDate: string | null;
}
